import React, { useState } from "react";
import clsx from "clsx";

import {
  ITutorialJourneyActivatorDto,
  ITutorialDto,
  TutorialJourneyType,
} from "@growth-machine-llc/stridist-api-client";
import makeStyles from "@mui/styles/makeStyles";
import {
  Box,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { ADMIN_USERS_ROUTE } from "../../../routes/routes";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import TutorialJourneyActivatorsService from "../../../services/TutorialJourneyActivatorsService";
import { TUTORIAL_ACTIVATORS_QUERY_KEY } from "./TutorialJourneysList";

const useStyles = makeStyles((theme) => ({
  link: {
    textDecoration: "underline",
    color: theme.palette.text.primary,
    "&:hover": {
      textDecoration: "none",
      color: theme.palette.text.secondary,
    },
  },
  value: {
    fontSize: 14,
    fontWeight: 500,
  },
  description: {
    maxWidth: 360,
    whiteSpace: "normal",
    wordBreak: "break-word",
    display: "block",
    margin: 0,
    cursor: "pointer",
    color: theme.palette.text.secondary,
  },
}));

interface JourneyActivatorsRowProps {
  activator: ITutorialJourneyActivatorDto;
  journeys: ITutorialDto[];
  onForwardClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    activator: ITutorialJourneyActivatorDto,
  ) => void;
  onDeleteClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    activator: ITutorialJourneyActivatorDto,
  ) => void;
}

export const JourneyActivatorRow = ({
  activator,
  journeys,
  onForwardClick,
  onDeleteClick,
}: JourneyActivatorsRowProps) => {
  const s = useStyles();
  const queryClient = useQueryClient();
  const [descOpen, setDescOpen] = useState(false);
  const [descDraft, setDescDraft] = useState<string>(activator.description || "");

  const { mutate: updateActivator, isPending: updating } = useMutation({
    mutationFn: (payload: { id: number; description: string | null }) =>
      TutorialJourneyActivatorsService.updateJourneyActivator({
        id: activator.id,
        description: payload.description,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TUTORIAL_ACTIVATORS_QUERY_KEY] });
      setDescOpen(false);
    },
  });

  const journey = journeys.find((j) => j.id === activator.tutorialJourneyId);

  return (
    <TableRow>
      <TableCell align="center">
        <Typography sx={{ mb: 4 }} className={s.value}>{journey ? journey.title : "-"}</Typography>
        <Typography
          className={s.description}
          component="div"
          onClick={() => {
            setDescDraft(activator.description || "");
            setDescOpen(true);
          }}
        >
          {activator.description || "-"}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {journey ? TutorialJourneyType[journey.type] : "-"}
      </TableCell>
      <TableCell align="center">
        {activator.createdBy ? (
          <Link
            href={`${ADMIN_USERS_ROUTE}/${activator.createdBy}`}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(s.link)}
          >
            <Typography className={clsx(s.value)} component="span">
              {activator.createdBy}
            </Typography>
            <ArrowOutwardIcon fontSize="small" style={{ marginLeft: 4 }} />
          </Link>
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell align="center">
        <Typography variant="caption">
          <b>Created:</b>{" "}
          {activator.created ? activator.created.format("MMM D, YYYY") : "-"}
        </Typography>
        <br />
        <Typography variant="caption">
          <b>Updated:</b>{" "}
          {activator.lastModified
            ? activator.lastModified.format("MMM D, YYYY")
            : "-"}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {activator.lastModifiedBy ? (
          <Link
            href={`${ADMIN_USERS_ROUTE}/${activator.lastModifiedBy}`}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(s.link)}
          >
            <Typography className={clsx(s.value)} component="span">
              {activator.lastModifiedBy}
            </Typography>
            <ArrowOutwardIcon fontSize="small" style={{ marginLeft: 4 }} />
          </Link>
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell align="center">
        <IconButton onClick={(e) => onForwardClick(e, activator)} size="large">
          <ArrowForwardRounded />
        </IconButton>
        <IconButton onClick={(e) => onDeleteClick(e, activator)} size="large">
          <DeleteForeverIcon />
        </IconButton>
      </TableCell>
      <Dialog open={descOpen} onClose={() => setDescOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit description</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            minRows={3}
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDescOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={updating}
            onClick={() =>
              updateActivator({ id: activator.id, description: descDraft.trim() ? descDraft.trim() : null })
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
};
